import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { getInitials } from '../utils/data/get-initials.ts';
import type UserModel from './user.ts';
import type AccountModel from './account.ts';
import type EmailAddressModel from './email-address.ts';
import type EmployeeModel from './employee.ts';
import type PhoneNumberModel from './phone-number.ts';
import type PostalAddressModel from './postal-address.ts';
import type StrategyFinderResultModel from './strategy-finder-result.ts';
import type MessageModel from './message.ts';
import type AccountTransactionModel from './account-transaction.ts';
import type ObservableDocument from './observable-document.ts';
import { Type } from '@warp-drive/core-types/symbols';

export default class PersonModel extends Model {
  @attr declare firstName: string;
  @attr declare lastName: string;
  @attr declare preferences: Record<string, unknown>;
  @attr declare accountReceivingContributionsId: string;
  @attr declare updatedAt: string;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('user', { async: false, inverse: 'person' })
  declare user: UserModel;
  @hasMany('account', { async: false, inverse: 'person' })
  declare accounts: AccountModel[];
  @hasMany('email-address', { async: false, inverse: null })
  declare emailAddresses: EmailAddressModel[];
  @hasMany('employee', { async: false, inverse: 'person' })
  declare employees: EmployeeModel[];
  @hasMany('phone-number', { async: false, inverse: null })
  declare phoneNumbers: PhoneNumberModel[];
  @hasMany('postal-address', { async: false, inverse: null })
  declare postalAddresses: PostalAddressModel[];
  @hasMany('strategy-finder-result', { async: false, inverse: null })
  declare strategyFinderResults: StrategyFinderResultModel[];
  @hasMany('message', { async: false, inverse: 'person' })
  declare messages: MessageModel[];
  @hasMany('account-transaction', { async: false, inverse: 'person' })
  declare accountTransactions: AccountTransactionModel[];
  @hasMany('observable-document', { async: false, inverse: 'person' })
  declare observableDocuments: ObservableDocument[];

  /**************************
   **  Computed Properties **
   **************************/

  get fullName() {
    return [this.firstName, this.lastName].join(' ');
  }

  get initials() {
    return getInitials(this);
  }

  get primaryEmail() {
    return this.emailAddresses.find((e) => e.contactType === 'PRIMARY');
  }

  get primaryPhoneNumber() {
    return this.phoneNumbers.find((e) => e.phoneNumberType === 'MOBILE');
  }

  get accountStats() {
    const totalDebt = this.totalLoanBalance;
    let totalLoans = 0;

    // TODO: Somehow just do all the loops once.
    this.activeAccounts.forEach((account) => {
      totalLoans += account.activeLoans.length;
    });

    return {
      totalDebt,
      totalLoans,
    };
  }

  get accountReceivingContributions(): AccountModel | null {
    return (
      this.activeAccounts.find(({ id }) => id === this.accountReceivingContributionsId) || null
    );
  }

  get activeAccounts() {
    return (this.accounts?.filter((e) => !e.deactivatedOn) || [])
      .filter((account) => !account.observation?.['is-paid-off'])
      .sort((a, b) => a.publicOrdinalValue - b.publicOrdinalValue);
  }

  get paidAccounts() {
    return (this.accounts?.filter((e) => !e.deactivatedOn) || []).filter(
      (account) => account.observation?.['is-paid-off'] === true
    );
  }

  get inactiveAccounts() {
    return this.accounts?.filter((e) => !!e.deactivatedOn) || [];
  }

  get totalLoanBalance() {
    let balance = 0;

    this.activeAccounts.forEach(
      (account) => (balance += account.deactivatedOn ? 0 : account.loanTotal)
    );
    return balance;
  }

  get weightedInterestRate() {
    return (
      (
        this.activeAccounts.reduce(
          // @ts-expect-error: no type for this
          (a, { loanInterestAverage: b }) => a + parseFloat(b || 0.0),
          0.0
        ) / this.activeAccounts.length
      ).toFixed(2) || '0.00'
    );
  }

  get allActiveAccountLoans() {
    return this.activeAccounts
      .reduce((allLoans, account) => {
        const loans = account.activeLoans.slice();
        // @ts-expect-error: all loans has a mismatch type
        return allLoans.concat(loans);
      }, [])
      .flat();
  }

  get needsToLinkLoans() {
    return this.activeAccounts.length === 0;
  }

  get reasonForNotLinkingLoans() {
    return this.preferences['reason-for-not-linking-loans'] as string;
  }

  get needsToAnswerWhyNoLinkedLoans() {
    return !!(this.needsToLinkLoans && !this.reasonForNotLinkingLoans);
  }

  get activeEmployee() {
    return this.employees.find((employee) => employee.isActive);
  }

  get invites() {
    return this.employees
      .filter((employee) => employee.isActive)
      .map((employee) => employee.invites)
      .flat();
  }

  [Type] = 'person' as const;
}
